import { useMemo } from "react";
import {
  ArrayParam,
  type QueryParamConfigMap,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { typedObjectEntries } from "common/helpers/utils";
import type { WorkspaceAccount } from "common/types";

import type { AccountSearchParams } from "../types";

const defaultSearchParams: AccountSearchParams = {
  state: [],
  seat: [],
  linkedinSubscription: [],
};

const paramsConfig = typedObjectEntries(defaultSearchParams).reduce(
  (accumulator, [key, value]) => {
    accumulator[key] = withDefault(ArrayParam, value);
    return accumulator;
  },
  {} as QueryParamConfigMap,
);
export default function useAccountsFilters(accounts: WorkspaceAccount[]) {
  const [searchText, setSearchText] = useQueryParam(
    "search",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );
  const [searchParams, setSearchParams] = useQueryParams(paramsConfig, {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  }) as [
    AccountSearchParams,
    (searchParams: Partial<AccountSearchParams>) => void,
  ];

  const filteredAccounts = useMemo(() => {
    return accounts
      ? accounts.filter(
          ({ full_name, state, license, premium, sales_navigator, type }) => {
            const isNameValid = full_name
              .trim()
              .toLowerCase()
              .includes(searchText.trim().toLowerCase());

            const isStateValid = searchParams.state.length
              ? searchParams.state.includes(state)
              : true;

            let isLicenseValid = true;
            if (searchParams.seat.length) {
              isLicenseValid =
                (searchParams.seat.includes("no-seat") &&
                  !license &&
                  type === "user") ||
                (searchParams.seat.includes("outreach") &&
                  !!license &&
                  type === "user") ||
                (searchParams.seat.includes("rented") && type === "rental");
            }

            let isSubscriptionValid = true;
            if (searchParams.linkedinSubscription.length) {
              isSubscriptionValid =
                (searchParams.linkedinSubscription.includes("premium") &&
                  premium) ||
                (searchParams.linkedinSubscription.includes("free") &&
                  !premium) ||
                (searchParams.linkedinSubscription.includes(
                  "sales-navigator",
                ) &&
                  sales_navigator);
            }

            return (
              isNameValid &&
              isStateValid &&
              isLicenseValid &&
              isSubscriptionValid
            );
          },
        )
      : [];
  }, [searchParams, searchText, accounts]);

  function clearSearchParams() {
    setSearchParams(defaultSearchParams);
  }

  return {
    searchText,
    setSearchText,
    searchParams,
    setSearchParams,
    hasSearchParams: Object.values(searchParams).some(
      (value) => !!value.length,
    ),
    clearSearchParams,
    filteredAccounts,
  };
}
